import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import AddPaymentModal from './AddPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  partially_paid: { label: 'Partially Paid', color: 'bg-yellow-100 text-yellow-800' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' }
};

const PAYMENT_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: null },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: null },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  reconciled: { label: 'Reconciled', color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/invoices/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch invoice');
      const data = await response.json();
      setInvoice(data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/procurement/invoices/payments?invoice_id=${id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch payments');
      const data = await response.json();
      setPayments(data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/procurement/invoices/${id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'Invoice PDF downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to download invoice PDF',
        variant: 'destructive'
      });
    }
  };

  const handleMarkAsSent = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/procurement/invoices/${id}/mark-sent`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to mark invoice as sent');

      toast({
        title: 'Success',
        description: 'Invoice marked as sent'
      });

      fetchInvoice();
    } catch (error) {
      console.error('Error marking invoice as sent:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark invoice as sent',
        variant: 'destructive'
      });
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#6C757D]">Loading invoice details...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#6C757D]">Invoice not found</div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/procurement/invoices')}
          className="flex items-center text-[#6C757D] hover:text-[#2B2D42] mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42] mb-2">
              Invoice {invoice.invoice_number}
            </h2>
            <div className="flex items-center space-x-4">
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              <span className="text-sm text-[#6C757D]">
                Created {formatDate(invoice.created_at)}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {invoice.status === 'draft' && (
              <Button
                onClick={handleMarkAsSent}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Mark as Sent
              </Button>
            )}
            <Button
              onClick={() => setShowAddPaymentModal(true)}
              className="bg-[#FF6B35] hover:bg-[#E5562D]"
              disabled={invoice.status === 'paid' || invoice.status === 'cancelled'}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parties */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3">From (Issuer)</h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42] text-base">
                      {invoice.company_snapshot?.legal_name || invoice.company_snapshot?.trading_name || 'N/A'}
                    </div>
                    <div>{invoice.company_snapshot?.address || 'N/A'}</div>
                    <div className="font-medium text-[#2B2D42]">
                      {invoice.company_snapshot?.country || 'United Arab Emirates'}
                    </div>
                    {invoice.company_snapshot?.license_number && (
                      <div className="mt-2">
                        <span className="font-semibold">License:</span> {invoice.company_snapshot.license_number}
                      </div>
                    )}
                    {invoice.company_snapshot?.trn && (
                      <div>
                        <span className="font-semibold">TRN:</span> {invoice.company_snapshot.trn}
                      </div>
                    )}
                    {!invoice.company_snapshot?.trn && (invoice.company_snapshot?.vat_number || invoice.company_snapshot?.tax_id) && (
                      <div>
                        <span className="font-semibold">Tax ID:</span> {invoice.company_snapshot.vat_number || invoice.company_snapshot.tax_id}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3">To (Recipient)</h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42]">
                      {invoice.supplier_snapshot?.company_name || invoice.supplier_snapshot?.name || 'N/A'}
                    </div>
                    <div>{invoice.supplier_snapshot?.email || 'N/A'}</div>
                    <div>{invoice.supplier_snapshot?.phone || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Items */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Invoice Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-sm font-semibold text-[#2B2D42]">Product</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Qty</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Unit Price</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Tax</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Discount</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items?.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-3 text-sm text-[#2B2D42]">
                          <div className="font-medium">{item.product_name}</div>
                          {item.description && (
                            <div className="text-xs text-[#6C757D] mt-1">{item.description}</div>
                          )}
                        </td>
                        <td className="py-3 text-sm text-right text-[#6C757D]">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="py-3 text-sm text-right text-[#6C757D]">
                          {formatCurrency(item.unit_price, invoice.currency)}
                        </td>
                        <td className="py-3 text-sm text-right text-[#6C757D]">
                          {formatCurrency(item.tax_amount, invoice.currency)}
                        </td>
                        <td className="py-3 text-sm text-right text-[#6C757D]">
                          {formatCurrency(item.discount_amount, invoice.currency)}
                        </td>
                        <td className="py-3 text-sm text-right font-medium text-[#2B2D42]">
                          {formatCurrency(item.total, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-6 flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Subtotal:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formatCurrency(invoice.subtotal, invoice.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Tax:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formatCurrency(invoice.tax_total, invoice.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Discount:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formatCurrency(invoice.discount_total, invoice.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-bold text-[#2B2D42]">Total Amount:</span>
                    <span className="font-bold text-lg text-[#FF6B35]">
                      {formatCurrency(invoice.total_amount, invoice.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Amount Paid:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(invoice.amount_paid, invoice.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-bold text-[#2B2D42]">Outstanding:</span>
                    <span className="font-bold text-lg text-[#FF6B35]">
                      {formatCurrency(invoice.amount_outstanding, invoice.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Payment History</h3>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-[#6C757D]">
                  <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No payments recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => {
                    const statusCfg = PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.pending;
                    const StatusIcon = statusCfg.icon;

                    return (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            {StatusIcon && <StatusIcon className="h-5 w-5 text-green-600" />}
                            <div>
                              <div className="font-medium text-[#2B2D42]">
                                {payment.payment_number}
                              </div>
                              <div className="text-sm text-[#6C757D]">
                                {payment.payment_method.replace('_', ' ').toUpperCase()} • {formatDate(payment.payment_date)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="font-bold text-[#2B2D42]">
                              {formatCurrency(payment.amount, payment.currency)}
                            </div>
                            {payment.transaction_id && (
                              <div className="text-xs text-[#6C757D]">
                                ID: {payment.transaction_id}
                              </div>
                            )}
                          </div>
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Invoice Info */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-sm font-semibold text-[#2B2D42] mb-4">Invoice Information</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[#6C757D]">Issue Date</div>
                  <div className="font-medium text-[#2B2D42]">{formatDate(invoice.issue_date)}</div>
                </div>
                <div>
                  <div className="text-[#6C757D]">Due Date</div>
                  <div className="font-medium text-[#2B2D42]">{formatDate(invoice.due_date)}</div>
                </div>
                <div>
                  <div className="text-[#6C757D]">Currency</div>
                  <div className="font-medium text-[#2B2D42]">{invoice.currency}</div>
                </div>
                <div>
                  <div className="text-[#6C757D]">Source</div>
                  <div className="font-medium text-[#2B2D42]">
                    {invoice.source_type === 'po' ? `Purchase Order (${invoice.source_id})` : 'Manual'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes & Terms */}
          {(invoice.notes || invoice.terms) && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-[#2B2D42] mb-4">Additional Information</h3>
                <div className="space-y-4 text-sm">
                  {invoice.notes && (
                    <div>
                      <div className="text-[#6C757D] mb-1">Notes</div>
                      <div className="text-[#2B2D42]">{invoice.notes}</div>
                    </div>
                  )}
                  {invoice.terms && (
                    <div>
                      <div className="text-[#6C757D] mb-1">Payment Terms</div>
                      <div className="text-[#2B2D42]">{invoice.terms}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <AddPaymentModal
          isOpen={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          invoice={invoice}
          onSuccess={() => {
            setShowAddPaymentModal(false);
            fetchInvoice();
            fetchPayments();
          }}
        />
      )}
    </div>
  );
};

export default InvoiceDetail;
